import { useMemo } from 'react'
import { Chart } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

export interface RadarSeries {
  label: string
  values: number[]
  color?: string
}

export interface RadarChartProps {
  /** Axis labels; one per radial arm. */
  labels: string[]
  series: RadarSeries[]
  height?: number | string
  className?: string
  ariaLabel?: string
}

const PALETTE = [
  '#2563eb', '#16a34a', '#dc2626', '#ca8a04', '#9333ea',
  '#0891b2', '#ea580c', '#4f46e5', '#db2777', '#0d9488',
]

export function RadarChart({
  labels,
  series,
  height = 320,
  className,
  ariaLabel,
}: RadarChartProps) {
  const dark = useDarkMode()

  const data = useMemo<ChartData<'radar'>>(
    () => ({
      labels,
      datasets: series.map((s, i) => ({
        label: s.label,
        data: s.values,
        backgroundColor: (s.color ?? PALETTE[i % PALETTE.length]) + '33',
        borderColor: s.color ?? PALETTE[i % PALETTE.length],
        borderWidth: 2,
        pointBackgroundColor: s.color ?? PALETTE[i % PALETTE.length],
      })),
    }),
    [labels, series],
  )

  const options = useMemo<ChartOptions<'radar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          angleLines: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          pointLabels: {
            color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight,
            font: { size: 11 },
          },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight, backdropColor: 'transparent' },
        },
      },
      plugins: {
        legend: { display: series.length > 1, position: 'top' },
        datalabels: { display: false },
      },
    }),
    [dark, series.length],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Chart type="radar" data={data} options={options} />
    </div>
  )
}
